import type { LookupPedidoRow, LookupTransactionData } from '../services/orders/transactionLookupService'
import { getLookupErrorMessage, lookupTransactionIds } from '../services/orders/transactionLookupService'
import { collectTransactionIdsFromOrderPayload } from './collectOrderTransactionIds'

export type ValidateTransactionIdsResult = { ok: true } | { ok: false; message: string }

/** Homecenter usa id_transaccion "1" como marcador; no debe bloquearse por lookup de duplicados */
function isHomecenterTransactionIdBypass(
  storeName: string | undefined | null,
  transactionId: string | undefined | null
): boolean {
  return String(storeName ?? '').trim() === 'Homecenter' && String(transactionId ?? '').trim() === '1'
}

function getStoreNameFromOrderPayload(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const d = data as Record<string, unknown>
  const fromRoot = String(d.store_name ?? '').trim()
  if (fromRoot) return fromRoot
  const extra = d.extraDetails
  if (extra && typeof extra === 'object') {
    const fromEx = String((extra as Record<string, unknown>).store_name ?? '').trim()
    if (fromEx) return fromEx
  }
  const meta = d.meta_data
  if (Array.isArray(meta)) {
    const entry = meta.find((m: { key?: string; value?: unknown }) => m?.key === 'store_name')
    if (entry?.value !== undefined && entry?.value !== null) {
      return String(entry.value).trim()
    }
  }
  return ''
}

function filterTransactionIdsForLookup(payload: unknown, ids: string[]): string[] {
  const storeName = getStoreNameFromOrderPayload(payload)
  return ids.filter((id) => !isHomecenterTransactionIdBypass(storeName, id))
}

function pedidoMatchesCurrent(p: LookupPedidoRow, currentOrderId: number): boolean {
  const cur = currentOrderId
  if (p.id_pedido != null && Number(p.id_pedido) === cur) return true
  if (p.id_pedido_sistema != null) {
    const sys = Number(p.id_pedido_sistema)
    if (!Number.isNaN(sys) && sys === cur) return true
    if (String(p.id_pedido_sistema).trim() === String(cur)) return true
  }
  return false
}

function isLookupConflictForCreate(data: LookupTransactionData): boolean {
  if (data.found !== true) return false
  const pedidos = data.pedidos
  if (Array.isArray(pedidos) && pedidos.length > 0) return true
  if (data.pedidos_pagos?.found && (data.pedidos_pagos.count ?? 0) > 0) return true
  if (data.pagos_bold?.found && (data.pagos_bold.count ?? 0) > 0) return true
  return data.found === true
}

function isLookupConflictForUpdate(data: LookupTransactionData, currentOrderId: number): boolean {
  if (data.found !== true) return false
  const pedidos = data.pedidos
  if (!Array.isArray(pedidos) || pedidos.length === 0) {
    if (data.pedidos_pagos?.found && (data.pedidos_pagos.count ?? 0) > 0) return true
    if (data.pagos_bold?.found && (data.pagos_bold.count ?? 0) > 0) return true
    return false
  }
  return pedidos.some((p) => !pedidoMatchesCurrent(p, currentOrderId))
}

/** Texto con id_pedido / id_pedido_sistema para el usuario (una fila del API). */
function formatPedidoRowForMessage(p: LookupPedidoRow): string {
  const parts: string[] = []
  if (p.id_pedido != null && String(p.id_pedido).trim() !== '') {
    parts.push(`id_pedido ${String(p.id_pedido).trim()}`)
  }
  if (p.id_pedido_sistema != null && String(p.id_pedido_sistema).trim() !== '') {
    parts.push(`id_pedido_sistema ${String(p.id_pedido_sistema).trim()}`)
  }
  return parts.length > 0 ? parts.join(', ') : 'pedido sin id_pedido en la respuesta'
}

/**
 * Describe qué pedido(s) tienen el ID de transacción, según `data.pedidos` u orígenes secundarios.
 */
function describeConflictingPedidosForMessage(
  data: LookupTransactionData,
  opts?: { currentOrderId?: number }
): string {
  const raw = Array.isArray(data.pedidos) ? data.pedidos : []
  const cur = opts?.currentOrderId
  const pedidos =
    cur != null && Number.isFinite(cur)
      ? raw.filter((p) => !pedidoMatchesCurrent(p, cur))
      : raw

  if (pedidos.length > 0) {
    const detalle = pedidos.map(formatPedidoRowForMessage).join(' · ')
    return pedidos.length === 1
      ? `Pedido que ya tiene ese ID: ${detalle}.`
      : `Pedidos que ya tienen ese ID: ${detalle}.`
  }

  if (data.pedidos_pagos?.found && (data.pedidos_pagos.count ?? 0) > 0) {
    return 'Coincidencia en pagos parciales (pedidos_pagos); el API no devolvió id_pedido en la lista pedidos.'
  }
  if (data.pagos_bold?.found && (data.pagos_bold.count ?? 0) > 0) {
    return 'Coincidencia en pagos Bold; el API no devolvió id_pedido en la lista pedidos.'
  }
  return 'El sistema indica que el ID ya está en uso.'
}

/**
 * Valida todos los IDs de transacción del payload antes de crear pedido.
 * En plan separe / pagos parciales puede haber varios IDs distintos; se consultan en lote al API (hasta 40 por petición).
 * Bloquea si el lookup indica que el ID ya existe en el sistema.
 */
export async function validateTransactionIdsForCreate(payload: unknown): Promise<ValidateTransactionIdsResult> {
  const ids = filterTransactionIdsForLookup(
    payload,
    collectTransactionIdsFromOrderPayload(payload)
  )
  if (ids.length === 0) return { ok: true }

  try {
    const results = await lookupTransactionIds(ids)
    for (const { id, data } of results) {
      if (isLookupConflictForCreate(data)) {
        const detallePedidos = describeConflictingPedidosForMessage(data)
        return {
          ok: false,
          message: `El ID de transacción "${id}" ya está registrado. ${detallePedidos} No se puede crear el pedido.`
        }
      }
    }
  } catch (e) {
    return { ok: false, message: getLookupErrorMessage(e) }
  }
  return { ok: true }
}

/**
 * Valida IDs antes de actualizar: permite reutilizar el mismo ID si solo corresponde al pedido actual.
 * Varios `id_transaccion` en filas de part/plan separe se validan todos (dedupe en la recolección).
 */
export async function validateTransactionIdsForUpdate(
  payload: unknown,
  currentOrderId: number
): Promise<ValidateTransactionIdsResult> {
  if (!Number.isFinite(currentOrderId) || currentOrderId <= 0) {
    return { ok: false, message: 'No se pudo determinar el pedido actual para validar transacciones.' }
  }

  const ids = filterTransactionIdsForLookup(
    payload,
    collectTransactionIdsFromOrderPayload(payload)
  )
  if (ids.length === 0) return { ok: true }

  try {
    const results = await lookupTransactionIds(ids)
    for (const { id, data } of results) {
      if (isLookupConflictForUpdate(data, currentOrderId)) {
        const detallePedidos = describeConflictingPedidosForMessage(data, { currentOrderId })
        return {
          ok: false,
          message: `El ID de transacción "${id}" ya está asociado a otro pedido. ${detallePedidos} No se puede actualizar.`
        }
      }
    }
  } catch (e) {
    return { ok: false, message: getLookupErrorMessage(e) }
  }
  return { ok: true }
}
